import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Fab, Card, CardContent, Snackbar, useTheme,
  alpha, Fade, LinearProgress, InputAdornment, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Close as CloseIcon, Search as SearchIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';

const CategoryPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { canViewCategories, canEditCategories, canDeleteCategories } = usePermissions();
  
  // State management
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Form data
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: ''
  });

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('get_expense_categories_decrypted', { company_id_param: companyId });
      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load expense categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    fetchCategories();
  }, [companyId]);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditMode(true);
      setFormData({
        id: category.id,
        name: category.name || '',
        description: category.description || ''
      });
    } else {
      setEditMode(false);
      setFormData({
        id: '',
        name: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setFormData({
      id: '',
      name: '',
      description: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required.');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      // Encrypt sensitive fields
      const { data: encryptedName, error: nameError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.name.trim() });
      if (nameError) throw nameError;

      const { data: encryptedDescription, error: descError } = await supabase
        .rpc('encrypt_expense_field', { field_value: formData.description.trim() || null });
      if (descError) throw descError;

      const categoryData = {
        company_id: companyId,
        name_encrypted: encryptedName,
        description_encrypted: encryptedDescription
      };

      if (editMode) {
        const { error } = await supabase
          .from('expense_categories')
          .update(categoryData)
          .eq('id', formData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('expense_categories')
          .insert(categoryData);
        if (error) throw error;
      }

      await fetchCategories();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: `Category ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving category:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} category.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      const { error } = await supabase
        .from('expense_categories')
        .delete()
        .eq('id', categoryToDelete.id);
      if (error) throw error;
      
      await fetchCategories();
      setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting category: ${err.message}`, severity: 'error' });
    }
    setDeleteConfirmOpen(false);
    setCategoryToDelete(null);
  };

  // Filter categories based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCategories(categories);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = categories.filter(cat => 
      cat.name?.toLowerCase().includes(searchLower) ||
      cat.description?.toLowerCase().includes(searchLower)
    );
    setFilteredCategories(filtered);
    setPage(0);
  }, [searchTerm, categories]);

  // Permission check
  if (!canViewCategories()) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ViewListIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Categories Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Categories section. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading && categories.length === 0) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <ViewListIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 3
            }}>
              Loading Expense Categories
            </Typography>
            <LinearProgress />
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Container maxWidth="xl" sx={{ py: 3, pb: 16 }}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Card sx={{ 
              position: 'relative',
              borderRadius: 3,
              mt: 5,
              mb: 3,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: theme.palette.grey[100],
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
                opacity: 1
              }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 3, 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}>
                    <ViewListIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 500, 
                      color: 'primary.main', 
                      mb: 1,
                      fontSize: '2rem',
                      letterSpacing: '-0.5px'
                    }}>
                      Expense Categories
                    </Typography>
                    <Typography variant="subtitle1" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      {companyName}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary', 
                  maxWidth: 700, 
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}>
                  Manage expense categories to organize and track your business expenses.
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Search Bar */}
          <Card sx={{
            height: 100,
            mb: 2,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 280,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      height: 40,
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      py: 1.25
                    }
                  }}
                />
                
                <Box sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {`${filteredCategories.length} categor${filteredCategories.length !== 1 ? 'ies' : 'y'}`}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Categories Table */}
          <Paper sx={{ 
            borderRadius: 3, 
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            }
           }}>
             <TableContainer sx={{ overflowX: 'hidden' }}>
               <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Category Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3 }}>
                      Description
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 3, width: 150 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((category) => (
                    <TableRow 
                      key={category.id}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: 'white',
                        '&:hover': { 
                          bgcolor: 'grey.50',
                          transform: 'translateX(4px)',
                          boxShadow: 'inset 4px 0 0 black'
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {category.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, px: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {category.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={canEditCategories() ? "Edit" : "No edit permission"}>
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => handleOpenDialog(category)}
                                disabled={!canEditCategories()}
                                sx={{
                                  color: canEditCategories() ? 'primary.main' : 'action.disabled',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 1,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': canEditCategories() ? {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                  } : {},
                                  '&.Mui-disabled': {
                                    color: 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDeleteCategories() ? "Delete" : "No delete permission"}>
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => handleDeleteClick(category)}
                                disabled={!canDeleteCategories()}
                                sx={{
                                  color: canDeleteCategories() ? 'error.main' : 'action.disabled',
                                  border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                  borderRadius: 1,
                                  transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                  '&:hover': canDeleteCategories() ? {
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                                  } : {},
                                  '&.Mui-disabled': {
                                    color: 'action.disabled',
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.2)}`
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCategories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No categories yet. Click the + button to add the first category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 3,
              mb: 4,
              px: 3
            }}>
              <Paper sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                overflow: 'hidden'
              }}>
                <TablePagination
                  component="div"
                  count={filteredCategories.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </Paper>
            </Box>
          </Paper>

          {canEditCategories() && (
            <Tooltip title="Add Category">
              <Fab
                color="primary"
                onClick={() => handleOpenDialog()}
                sx={{ 
                  position: 'fixed', 
                  bottom: 24, 
                  right: 24, 
                  width: 100, 
                  height: 100
                }}
              >
                <AddIcon sx={{ fontSize: 32 }} />
              </Fab>
            </Tooltip>
          )}

          {/* Add/Edit Dialog */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              {editMode ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Category Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                {submitting ? 'Saving...' : editMode ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this category?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirmDelete} variant="contained" color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Fade>
  );
};

export default CategoryPage;